import './styles/main.css';
import { site } from './content/collections.js';
import { loadGalleryCollections } from './content/loadRolls.js';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// ── State ──────────────────────────────────────────────────────────────
let collections = [];
let activeIndex  = 0;
let lightboxItems = [];
let lightboxIndex = 0;
/** @type {HTMLElement | null} */
let lightboxEl = null;

// ── Helpers ────────────────────────────────────────────────────────────
function el(tag, cls, attrs = {}) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  for (const [k, v] of Object.entries(attrs)) {
    if (v != null) node.setAttribute(k, String(v));
  }
  return node;
}

// ── Lightbox ───────────────────────────────────────────────────────────
function closeLightbox() {
  if (!lightboxEl) return;
  lightboxEl.remove();
  lightboxEl = null;
}

function updateLightboxContent() {
  if (!lightboxEl) return;
  const item    = lightboxItems[lightboxIndex];
  if (!item) return;

  const img     = lightboxEl.querySelector('.lb-img');
  const cap     = lightboxEl.querySelector('.lb-caption');
  const prevBtn = lightboxEl.querySelector('.lb-prev');
  const nextBtn = lightboxEl.querySelector('.lb-next');

  if (img && item.frame.src) {
    img.style.opacity = '0';
    img.src = item.frame.src;
    img.alt = item.frame.alt || '';
    img.onload  = () => { img.style.opacity = '1'; };
    img.onerror = () => { img.style.opacity = '1'; };
  }

  if (cap) {
    const num = item.frame.caption || '';
    cap.textContent = num
      ? `${item.collectionTitle} \u2014 ${num}`
      : item.collectionTitle;
  }

  if (prevBtn) prevBtn.disabled = lightboxIndex === 0;
  if (nextBtn) nextBtn.disabled = lightboxIndex === lightboxItems.length - 1;
}

function navigateLightbox(delta) {
  const next = lightboxIndex + delta;
  if (next < 0 || next >= lightboxItems.length) return;
  lightboxIndex = next;
  updateLightboxContent();
}

function openLightbox(items, index) {
  closeLightbox();
  lightboxItems = items;
  lightboxIndex = index;

  const lb = el('div', 'lightbox');
  lb.setAttribute('role', 'dialog');
  lb.setAttribute('aria-modal', 'true');
  lb.setAttribute('aria-label', 'Photo viewer');

  const closeBtn = el('button', 'lb-close', { type: 'button', 'aria-label': 'Close' });
  closeBtn.innerHTML = '&times;';
  closeBtn.addEventListener('click', closeLightbox);

  const prevBtn = el('button', 'lb-prev', { type: 'button', 'aria-label': 'Previous' });
  prevBtn.innerHTML = '&#8592;';
  prevBtn.addEventListener('click', () => navigateLightbox(-1));

  const nextBtn = el('button', 'lb-next', { type: 'button', 'aria-label': 'Next' });
  nextBtn.innerHTML = '&#8594;';
  nextBtn.addEventListener('click', () => navigateLightbox(1));

  const inner   = el('div', 'lb-inner');
  const imgWrap = el('div', 'lb-img-wrap');
  const img     = el('img', 'lb-img');
  imgWrap.appendChild(img);

  const cap = el('p', 'lb-caption');
  inner.append(imgWrap, cap);
  lb.append(closeBtn, prevBtn, inner, nextBtn);

  lb.addEventListener('click', (e) => { if (e.target === lb) closeLightbox(); });

  let touchStartX = 0;
  lb.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
  lb.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) navigateLightbox(dx < 0 ? 1 : -1);
  }, { passive: true });

  document.body.appendChild(lb);
  lightboxEl = lb;
  updateLightboxContent();
}

// ── Strip rendering ────────────────────────────────────────────────────
/**
 * @param {HTMLElement} strip
 * @param {{ id: string, title: string, frames: object[] }} col
 */
function renderStrip(strip, col) {
  strip.replaceChildren();

  const lbItems = col.frames.map((f) => ({
    frame: f,
    collectionId: col.id,
    collectionTitle: col.title,
  }));

  col.frames.forEach((frame, i) => {
    const item   = el('div', 'strip-item');
    const figure = el('figure', 'strip-figure');

    if (frame.src) {
      const skeleton = el('div', 'strip-skeleton');
      const img = el('img', 'strip-img', {
        alt: frame.alt || col.title,
        loading: 'eager',
        decoding: 'async',
      });
      img.src = frame.src;
      img.addEventListener('load',  () => { skeleton.remove(); img.style.opacity = '1'; }, { once: true });
      img.addEventListener('error', () => { skeleton.remove(); }, { once: true });
      figure.append(skeleton, img);
    }

    const cap = el('figcaption', 'strip-caption');
    cap.textContent = frame.caption || String(i + 1);
    figure.appendChild(cap);

    figure.addEventListener('click', () => openLightbox(lbItems, i));

    item.appendChild(figure);

    // Staggered fade-in
    const delay = prefersReducedMotion.matches ? 0 : i * 70;
    setTimeout(() => { item.classList.add('is-visible'); }, delay);

    strip.appendChild(item);
  });

  // Trailing spacer
  strip.appendChild(el('div', 'strip-tail'));
}

// ── Collection switching ───────────────────────────────────────────────
/**
 * @param {number} index
 * @param {{ navBtns: HTMLElement[], strip: HTMLElement, stripArea: HTMLElement }} parts
 */
function switchCollection(index, parts) {
  const clamped = Math.max(0, Math.min(collections.length - 1, index));
  activeIndex = clamped;
  const col = collections[clamped];
  if (!col) return;

  parts.navBtns.forEach((btn, i) => {
    btn.classList.toggle('is-active', i === clamped);
  });

  const run = () => {
    renderStrip(parts.strip, col);
    parts.stripArea.scrollLeft = 0;
  };

  if (prefersReducedMotion.matches || typeof document.startViewTransition !== 'function') {
    run();
  } else {
    document.startViewTransition(run);
  }
}

// ── Build page ─────────────────────────────────────────────────────────
/** @param {import('./content/collections.js').Collection[]} cols */
function buildPage(cols) {
  collections = cols;

  const rootEl = document.querySelector('[data-gallery-root]');
  if (!rootEl) return;
  rootEl.classList.add('page-root');

  // ── Header ──────────────────────────────────────────────────────────
  const header      = el('header', 'site-header');
  const headerInner = el('div', 'header-inner');

  // Brand block — mirrors PortfolioMain h1 + subtitle
  const brandBlock = el('div', 'brand-block');
  const brandName  = el('h1', 'brand-name');
  brandName.textContent = site.title;
  // mono subtitle, mirrors `font-mono text-[11px] text-black/40 mt-2 tracking-[0.06em]`
  const brandSub = el('p', 'brand-sub');
  brandSub.textContent = 'film photography';
  brandBlock.append(brandName, brandSub);

  // Home link — mirrors the Photography button on vinayak-kapoor.com
  const homeLink = el('a', 'home-link', { href: 'https://vinayak-kapoor.com' });
  homeLink.textContent = 'Home';

  headerInner.append(brandBlock, homeLink);
  header.appendChild(headerInner);

  // ── Gallery body (panel + filmstrip) ────────────────────────────────
  const body  = el('div', 'gallery-body');
  const panel = el('aside', 'gallery-panel');

  const nav     = el('nav', 'panel-nav');
  const navBtns = [];

  cols.forEach((col, i) => {
    const btn = el('button', 'panel-btn', { type: 'button' });
    btn.textContent = col.title;
    btn.addEventListener('click', () => switchCollection(i, parts));
    navBtns.push(btn);
    nav.appendChild(btn);
  });

  panel.appendChild(nav);

  // Strip container
  const stripArea = el('div', 'strip-area');
  const strip     = el('div', 'strip');
  stripArea.appendChild(strip);

  body.append(panel, stripArea);

  // ── Footer ──────────────────────────────────────────────────────────
  const footer     = el('footer', 'site-footer');
  const footerLink = el('a', 'footer-link', { href: 'https://vinayak-kapoor.com' });
  footerLink.textContent = 'vinayak-kapoor.com';
  const footerYear = el('span', 'footer-year');
  footerYear.textContent = String(new Date().getFullYear());
  footer.append(footerLink, footerYear);

  rootEl.append(header, body, footer);

  const parts = { navBtns, strip, stripArea };

  // Load first collection
  if (cols.length > 0) switchCollection(0, parts);

  // ── Mouse-wheel → horizontal scroll ─────────────────────────────────
  stripArea.addEventListener('wheel', (e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    e.preventDefault();
    stripArea.scrollLeft += e.deltaY;
  }, { passive: false });

  // ── Keyboard navigation ──────────────────────────────────────────────
  document.addEventListener('keydown', (e) => {
    if (lightboxEl) {
      if (e.key === 'Escape')     { e.preventDefault(); closeLightbox(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); navigateLightbox(1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); navigateLightbox(-1); }
      return;
    }
    const dx = Math.min(stripArea.clientWidth * 0.72, 480);
    const behavior = prefersReducedMotion.matches ? 'auto' : 'smooth';
    if (e.key === 'ArrowRight') { e.preventDefault(); stripArea.scrollBy({ left:  dx, behavior }); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); stripArea.scrollBy({ left: -dx, behavior }); }
    if (e.key === 'ArrowDown')  { e.preventDefault(); switchCollection(activeIndex + 1, parts); }
    if (e.key === 'ArrowUp')    { e.preventDefault(); switchCollection(activeIndex - 1, parts); }
  });
}

// ── Bootstrap ──────────────────────────────────────────────────────────
async function init() {
  try {
    const cols = await loadGalleryCollections();
    buildPage(cols);
  } catch (err) {
    console.error('[gallery] Failed to load collections:', err);
    buildPage([]);
  }
}

init();
